import { DOMService, Logger, debounce } from '@arts/utilities';
import { CSS_CLASSES, DEFAULT_CONFIG } from './constants';
/**
 * AJAX-powered WordPress comment form handler
 *
 * Provides seamless comment submission without page reloads, with support for:
 * - Form validation and error handling
 * - Real-time UI feedback during submission
 * - Dynamic comment template updates
 * - Integration with WordPress comment-reply.js for threaded comments
 * - Smooth animations for new comments and response messages
 */
export class ArtsAJAXFormComments {
    // Core dependencies
    domService;
    logger;
    // Instance state
    initialized = false;
    config;
    // Cached DOM elements - updated dynamically to handle form movement by comment-reply.js
    elements = {
        form: null,
        commentsArea: null,
        commentsList: null,
        commentsListTitle: null,
        response: null,
        responseInner: null,
        responseIcon: null,
        responseText: null
    };
    // Bound event handlers to maintain proper context
    handlers = {
        submit: this.onSubmit.bind(this),
        fetchBefore: this.onFetchBefore.bind(this),
        fetchAfter: this.onFetchAfter.bind(this),
        fetchSuccess: this.onFetchSuccess.bind(this),
        fetchError: this.onFetchError.bind(this)
    };
    // Submission state tracking
    submitting = false;
    // Debounced form event dispatcher to prevent duplicate events
    debouncedFormAddedEvent = debounce((form) => {
        const document = this.domService.getDocumentElement();
        const event = new CustomEvent('arts/forms/form_added', {
            detail: { form },
            bubbles: true
        });
        document.dispatchEvent(event);
    }, 100);
    constructor(config) {
        this.domService = DOMService;
        this.logger = Logger;
        this.config = config;
    }
    // Public API
    get isInitialized() {
        return this.initialized;
    }
    get isSubmitting() {
        return this.submitting;
    }
    /**
     * Initialize the comment form handler
     * Sets up event listeners and prepares the form for AJAX submissions
     */
    init() {
        if (this.initialized) {
            return this;
        }
        this.attachEvents();
        this.initialized = true;
        return this;
    }
    /**
     * Clean up resources and remove event listeners
     * Safely destroys the instance and resets state
     */
    destroy() {
        if (!this.initialized) {
            return this;
        }
        this.detachEvents();
        this.resetElements();
        this.initialized = false;
        return this;
    }
    // DOM Management
    /**
     * Cache DOM elements for performance
     * Elements are stored for reuse but may be refreshed when form is moved by comment-reply.js
     */
    cacheElements() {
        const document = this.domService.getDocument();
        // Start with main containers from document root
        const form = this.elements.form ||
            this.domService.querySelector(document, `.${this.config.selectors.form}`);
        const commentsArea = this.domService.querySelector(document, `.${this.config.selectors.comments_area}`);
        // Response elements are injected into the form during submission
        const response = form
            ? this.domService.querySelector(form, `.${this.config.selectors.response}`)
            : null;
        const responseInner = response
            ? this.domService.querySelector(response, `.${this.config.selectors.response_inner}`)
            : null;
        const responseIcon = response
            ? this.domService.querySelector(response, `.${this.config.selectors.response_icon}`)
            : null;
        const responseText = response
            ? this.domService.querySelector(response, `.${this.config.selectors.response_text}`)
            : null;
        // Comments list elements live within the comments area
        const commentsList = commentsArea
            ? this.domService.querySelector(commentsArea, `.${this.config.selectors.comments_list}`)
            : null;
        const commentsListTitle = commentsArea
            ? this.domService.querySelector(commentsArea, `.${this.config.selectors.comments_list_title}`)
            : null;
        this.elements = {
            form,
            commentsArea,
            commentsList,
            commentsListTitle,
            response,
            responseInner,
            responseIcon,
            responseText
        };
    }
    /**
     * Reset cached elements to null state
     * Used during cleanup to prevent memory leaks
     */
    resetElements() {
        this.elements = {
            form: null,
            commentsArea: null,
            commentsList: null,
            commentsListTitle: null,
            response: null,
            responseInner: null,
            responseIcon: null,
            responseText: null
        };
    }
    /**
     * Get current form elements without caching
     *
     * Critical for WordPress comment-reply.js integration, which dynamically moves
     * the comment form around the DOM when replying to specific comments.
     * This ensures we always work with the form's current location.
     */
    getCurrentFormElements() {
        const document = this.domService.getDocument();
        // Always query fresh - the form location may have changed
        const form = this.domService.querySelector(document, `.${this.config.selectors.form}`);
        const commentsArea = this.domService.querySelector(document, `.${this.config.selectors.comments_area}`);
        // Response elements are dynamically created within the current form
        const response = form
            ? this.domService.querySelector(form, `.${this.config.selectors.response}`)
            : null;
        const responseInner = response
            ? this.domService.querySelector(response, `.${this.config.selectors.response_inner}`)
            : null;
        const responseIcon = response
            ? this.domService.querySelector(response, `.${this.config.selectors.response_icon}`)
            : null;
        const responseText = response
            ? this.domService.querySelector(response, `.${this.config.selectors.response_text}`)
            : null;
        // Comments list elements remain in their fixed container
        const commentsList = commentsArea
            ? this.domService.querySelector(commentsArea, `.${this.config.selectors.comments_list}`)
            : null;
        const commentsListTitle = commentsArea
            ? this.domService.querySelector(commentsArea, `.${this.config.selectors.comments_list_title}`)
            : null;
        return {
            form,
            commentsArea,
            commentsList,
            commentsListTitle,
            response,
            responseInner,
            responseIcon,
            responseText
        };
    }
    // Event Management
    /**
     * Attach global event listeners
     * Uses event delegation to handle form submissions anywhere in the document
     */
    attachEvents() {
        const document = this.domService.getDocument();
        document.addEventListener('submit', this.handlers.submit);
    }
    /**
     * Remove global event listeners during cleanup
     */
    detachEvents() {
        const document = this.domService.getDocument();
        document.removeEventListener('submit', this.handlers.submit);
    }
    /**
     * Handle form submission events
     * Validates the form target and prevents default submission for AJAX processing
     */
    onSubmit(event) {
        const target = event.target;
        if (!target || target.tagName !== 'FORM') {
            return;
        }
        if (!target.classList.contains(this.config.selectors.form)) {
            return;
        }
        event.preventDefault();
        if (this.submitting) {
            return;
        }
        this.cacheElements();
        if (!target.checkValidity()) {
            target.reportValidity();
            return;
        }
        this.elements.form = target;
        this.submitForm();
    }
    // AJAX Submission
    /**
     * Submit form data via AJAX to WordPress backend
     * Handles the complete submission flow with proper error handling
     */
    async submitForm() {
        if (!this.elements.form) {
            return;
        }
        const formData = new FormData(this.elements.form);
        formData.append('action', this.config.action);
        formData.append('_ajax_nonce', this.config.nonce);
        this.handlers.fetchBefore();
        try {
            const response = await fetch(this.config.ajaxUrl, {
                method: 'POST',
                body: formData,
                credentials: 'same-origin'
            });
            const data = await response.json();
            if (data.success) {
                this.handlers.fetchSuccess(data);
            }
            else {
                this.handlers.fetchError(data);
            }
        }
        catch (error) {
            this.logger.error('Network error during submission', error);
            this.handlers.fetchError({
                success: false,
                data: {
                    message: 'Network error. Please try again.',
                    icon: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 8V12M12 16H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
                }
            });
        }
        finally {
            this.handlers.fetchAfter();
        }
    }
    // Submission State Handlers
    /**
     * Pre-submission handler - sets loading state and disables form
     * Prepares UI for submission by hiding previous responses and disabling interactions
     */
    onFetchBefore() {
        this.submitting = true;
        // Reset any previous response state
        this.hideResponse();
        // Get current form location (may have moved due to comment-reply.js)
        const currentForm = this.getCurrentFormElements().form;
        if (currentForm) {
            currentForm.classList.add(CSS_CLASSES.SUBMITTING);
            const formElements = currentForm.querySelectorAll('input, textarea, button, select');
            formElements.forEach((element) => {
                ;
                element.disabled = true;
            });
        }
    }
    /**
     * Post-submission handler - resets loading state and re-enables form
     * Cleans up submission state regardless of success or failure
     */
    onFetchAfter() {
        this.submitting = false;
        // Get current form location (may have moved due to comment-reply.js)
        const currentForm = this.getCurrentFormElements().form;
        if (currentForm) {
            currentForm.classList.remove(CSS_CLASSES.SUBMITTING);
            currentForm.classList.remove(CSS_CLASSES.SUCCESS, CSS_CLASSES.ERROR);
            const formElements = currentForm.querySelectorAll('input, textarea, button, select');
            formElements.forEach((element) => {
                ;
                element.disabled = false;
            });
        }
    }
    /**
     * Successful submission handler
     * Updates the comments template, resets form if needed, and shows success message
     */
    onFetchSuccess(response) {
        // Get current form location (may have moved due to comment-reply.js)
        const currentForm = this.getCurrentFormElements().form;
        if (currentForm) {
            currentForm.classList.add(CSS_CLASSES.SUCCESS);
        }
        this.updateCommentsTemplate(response.data.fragments.comments_template);
        if (response.data.reset_form && currentForm) {
            currentForm.reset();
        }
        this.highlightNewComment();
        this.showMessage(response.data.message, response.data.icon);
    }
    /**
     * Failed submission handler
     * Adds error styling and displays error message to user
     */
    onFetchError(response) {
        // Get current form location (may have moved due to comment-reply.js)
        const currentForm = this.getCurrentFormElements().form;
        if (currentForm) {
            currentForm.classList.add(CSS_CLASSES.ERROR);
        }
        this.showMessage(response.data.message, response.data.icon);
    }
    // UI Updates
    /**
     * Update the comments template with new content from server
     * Replaces the existing comments area with updated HTML including new comment
     */
    updateCommentsTemplate(newTemplate) {
        // Get current comments area (fresh query in case DOM changed)
        const currentCommentsArea = this.getCurrentFormElements().commentsArea;
        if (!currentCommentsArea) {
            this.logger.warn('Comments area not found, cannot update template');
            return;
        }
        const document = this.domService.getDocument();
        const tempContainer = document.createElement('div');
        tempContainer.innerHTML = newTemplate;
        const newCommentsArea = this.domService.querySelector(tempContainer, `.${this.config.selectors.comments_area}`);
        if (newCommentsArea) {
            currentCommentsArea.innerHTML = newCommentsArea.innerHTML;
            // Refresh element cache after DOM update
            this.cacheElements();
            // Notify ArtsForms about the updated form
            const updatedForm = this.getCurrentFormElements().form;
            if (updatedForm) {
                this.dispatchFormAddedEvent(updatedForm);
            }
        }
        else {
            this.logger.warn('New comments area not found in template');
        }
    }
    /**
     * Highlight and scroll to the newly added comment
     * Provides visual feedback with smooth animations to draw attention to new content
     */
    highlightNewComment() {
        const document = this.domService.getDocument();
        const newComment = this.domService.querySelector(document, `.${this.config.classes.ajax_added}`);
        if (newComment) {
            // Smooth scroll to center the new comment in viewport
            newComment.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
            });
            // Apply highlight styling immediately
            newComment.classList.add(CSS_CLASSES.COMMENT_HIGHLIGHT);
            // Trigger reveal animation after brief delay for better visual effect
            setTimeout(() => {
                newComment.classList.add(CSS_CLASSES.COMMENT_REVEAL);
            }, 100);
            // Remove highlight after animation completes
            setTimeout(() => {
                newComment.classList.remove(CSS_CLASSES.COMMENT_HIGHLIGHT);
            }, DEFAULT_CONFIG.HIGHLIGHT_DURATION);
        }
    }
    /**
     * Display response message to user with icon and animation
     * Handles both success and error messages with consistent styling
     */
    showMessage(message, icon) {
        // Always get fresh elements since form may have moved
        const currentFormElements = this.getCurrentFormElements();
        if (!currentFormElements.response ||
            !currentFormElements.responseIcon ||
            !currentFormElements.responseText) {
            this.logger.warn('Response elements not found, cannot show message');
            return;
        }
        // Check if already visible to avoid redundant animations
        const isAlreadyVisible = currentFormElements.form?.classList.contains(this.config.classes.visible_response);
        // Update response content
        currentFormElements.responseIcon.innerHTML = icon;
        currentFormElements.responseText.textContent = message;
        // Animate only if not already showing
        if (!isAlreadyVisible) {
            this.animateResponseDisplay(currentFormElements.response);
        }
        // Show the response with visibility class
        if (currentFormElements.form) {
            currentFormElements.form.classList.add(this.config.classes.visible_response);
        }
    }
    // Animation Helpers
    /**
     * Animate response element with smooth height transition
     * Prevents animation conflicts and provides smooth reveal effect
     *
     * @param responseElement The response container to animate
     */
    animateResponseDisplay(responseElement) {
        // Prevent multiple simultaneous animations
        if (responseElement.classList.contains(CSS_CLASSES.ANIMATING_RESPONSE)) {
            return;
        }
        // Mark as animating
        this.domService.addClass(responseElement, CSS_CLASSES.ANIMATING_RESPONSE);
        // Set collapsed state
        responseElement.style.height = '0px';
        responseElement.style.opacity = '0';
        // Force browser reflow to apply initial state
        responseElement.offsetHeight;
        // Calculate natural height and animate to it
        const naturalHeight = responseElement.scrollHeight;
        responseElement.style.height = `${naturalHeight}px`;
        responseElement.style.opacity = '1';
        // Clean up after animation completes
        setTimeout(() => {
            responseElement.style.height = 'auto';
            this.domService.removeClass(responseElement, CSS_CLASSES.ANIMATING_RESPONSE);
        }, DEFAULT_CONFIG.ANIMATION_DURATION);
    }
    /**
     * Hide response element and reset to collapsed state
     * Used to clean up response display before new submissions
     */
    hideResponse() {
        // Always get current elements since form may have moved
        const currentFormElements = this.getCurrentFormElements();
        if (!currentFormElements.response || !currentFormElements.form)
            return;
        // Remove visibility and reset styling
        currentFormElements.form.classList.remove(this.config.classes.visible_response);
        currentFormElements.response.style.height = '0px';
        currentFormElements.response.style.opacity = '0';
    }
    /**
     * Dispatch custom event to notify ArtsForms about form updates
     * This ensures floating labels are properly updated after DOM changes
     * Uses debouncing to prevent duplicate events during rapid updates
     */
    dispatchFormAddedEvent(form) {
        this.debouncedFormAddedEvent(form);
    }
    /**
     * Refresh cached elements after dynamic DOM changes
     * Useful when external scripts modify the DOM structure
     */
    refreshElements() {
        if (this.initialized) {
            this.cacheElements();
        }
        return this;
    }
}
